import { getTruck, setTruck } from '@theme/dialed-truck';
import { YmmPicker, truckLabel, yearsOf } from '@theme/dialed-ymm';

/**
 * <dialed-fit-guide>: the Fit guide checker (Make → Model → Year · Bed with the kit, size and
 * price) and the full truck list (search, make chips, midsize / full-size filter). Picking a row
 * fills the checker. Truck data comes from snippets/dialed-truck-data.liquid; Starter prices per
 * fit from the [data-fit-prices] JSON the section renders from the kit product.
 */
class DialedFitGuide extends HTMLElement {
  /** @type {Array<{handle:string,label:string,make:string,model:string,years:string,bed:string,width:number,length:number,size:string}>} */
  fits = [];
  /** @type {Record<string, [number, number]>} */
  prices = {};
  make = '';
  size = '';
  query = '';
  /** @type {string | null} */
  picked = null;
  /** @type {YmmPicker | null} */
  picker = null;

  connectedCallback() {
    try {
      this.fits = JSON.parse(this.querySelector('[data-dialed-trucks]')?.textContent ?? '[]');
      this.prices = JSON.parse(this.querySelector('[data-fit-prices]')?.textContent ?? '{}');
    } catch {
      return;
    }
    const select = (/** @type {string} */ name) => /** @type {HTMLSelectElement} */ (this.querySelector(`[data-fit="${name}"]`));
    this.picker = new YmmPicker(this.fits, { year: select('year'), make: select('make'), model: select('model'), bed: select('config') });
    this.picker.bind(() => this.#picked(true));
    this.querySelector('[data-r-go]')?.addEventListener('click', (event) => this.#go(event));

    const search = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-fit-search]'));
    search?.addEventListener('input', () => {
      this.query = search.value;
      this.renderList();
    });
    this.querySelectorAll('[data-fit-size]').forEach((button) =>
      button.addEventListener('click', () => {
        this.size = button.getAttribute('data-fit-size') ?? '';
        this.renderList();
      })
    );
    this.querySelector('[data-fit-makes]')?.addEventListener('click', (event) => {
      const chip = /** @type {HTMLElement} */ (event.target).closest('[data-fit-make]');
      if (!chip) return;
      this.make = chip.getAttribute('data-fit-make') ?? '';
      this.renderList();
    });
    this.querySelector('[data-fit-rows]')?.addEventListener('click', (event) => {
      const row = /** @type {HTMLElement} */ (event.target).closest('[data-fit-pick]');
      const fit = this.fits.find((f) => f.handle === row?.getAttribute('data-fit-pick'));
      if (!fit) return;
      this.pick({ fit: fit.handle });
      this.querySelector('#check')?.scrollIntoView({ behavior: 'smooth' });
    });
    this.querySelector('[data-fit-reset]')?.addEventListener('click', () => {
      this.make = '';
      this.size = '';
      this.query = '';
      if (search) search.value = '';
      this.renderList();
    });

    const stored = getTruck();
    if (stored) this.pick({ year: stored.year, make: stored.make, model: stored.model, fit: stored.fit }, false);
    else this.pick({}, false);
  }

  /** Short name: "Ram 1500" for numeric models, otherwise just the model. */
  #short(fit) {
    return /^\d/.test(fit.model) ? `${fit.make} ${fit.model}` : fit.model;
  }

  #isMid(fit) {
    return /mid/i.test(fit.size ?? '');
  }

  #money(cents) {
    try {
      return new Intl.NumberFormat(this.dataset.locale, {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
        minimumFractionDigits: cents % 100 ? 2 : 0,
      }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)}`;
    }
  }

  /**
   * @param {{ year?: string, make?: string, model?: string, fit?: string }} next
   * @param {boolean} [save]
   */
  pick(next, save = true) {
    this.picker?.set(next);
    this.#picked(save);
  }

  /** @param {boolean} save */
  #picked(save) {
    const fit = this.picker?.fit() ?? null;
    const year = this.picker?.state.year ?? '';
    this.picked = fit?.handle ?? null;
    if (save && fit) setTruck({ year, make: fit.make, model: fit.model, fit: fit.handle, label: truckLabel(fit, year) });
    this.renderResult(fit);
    this.renderList();
  }

  renderResult(fit) {
    const empty = /** @type {HTMLElement} */ (this.querySelector('[data-fit-empty]'));
    const result = /** @type {HTMLElement} */ (this.querySelector('[data-fit-result]'));
    empty.hidden = Boolean(fit);
    result.hidden = !fit;
    if (!fit) return;
    const [price, compare] = this.prices[fit.handle] ?? [0, 0];
    const set = (selector, text) => {
      const el = this.querySelector(selector);
      if (el) el.textContent = text;
    };
    const year = this.picker?.state.year;
    set('[data-r-line]', `${year ? `${year} ` : ''}${this.#short(fit)} · ${fit.bed ? `${fit.bed} bed` : this.dataset.oneBed}`);
    set('[data-r-kit]', `${fit.size}${this.dataset.kitSuffix ?? ''}`);
    set('[data-r-size]', fit.width && fit.length ? `${fit.width} × ${fit.length} in` : '');
    set('[data-r-price]', price ? this.#money(price) : '');
    set('[data-r-compare]', compare > price ? this.#money(compare) : '');
    const note = /** @type {HTMLElement | null} */ (this.querySelector('[data-r-note]'));
    if (note) {
      const months = Number(this.dataset.months) || 12;
      note.textContent = price ? (note.dataset.template ?? '').replace('[monthly]', this.#money(Math.round(price / months))) : '';
    }
    const go = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-r-go]'));
    const url = this.dataset.kitUrl;
    if (go && url) go.href = `${url}${url.includes('?') ? '&' : '?'}truck=${encodeURIComponent(fit.handle)}&year=${encodeURIComponent(year ?? '')}`;
  }

  #go(event) {
    const fit = this.fits.find((f) => f.handle === this.picked);
    if (!fit) event.preventDefault();
  }

  renderList() {
    const words = this.query.toLowerCase().split(/\s+/).filter(Boolean);
    const bySize = this.fits.filter((f) => !this.size || (this.size === 'mid' ? this.#isMid(f) : !this.#isMid(f)));
    const bySearch = bySize.filter((f) => {
      const text = `${f.make} ${f.model} ${f.years} ${f.bed} ${yearsOf(f).join(' ')}`.toLowerCase();
      return words.every((word) => text.includes(word));
    });

    // Make chips with counts for the current size + search filter
    const chips = this.querySelector('[data-fit-makes]');
    if (chips) {
      const makes = [...new Set(this.fits.map((f) => f.make))].sort();
      const chip = (value, label, count) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'dl-fit__chip';
        button.setAttribute('data-fit-make', value);
        button.setAttribute('aria-pressed', String(this.make === value));
        button.append(label, ' ');
        const span = document.createElement('span');
        span.textContent = String(count);
        button.append(span);
        return button;
      };
      chips.replaceChildren(
        chip('', this.dataset.all ?? 'All', bySearch.length),
        ...makes.map((m) => chip(m, m, bySearch.filter((f) => f.make === m).length))
      );
    }
    this.querySelectorAll('[data-fit-size]').forEach((button) =>
      button.setAttribute('aria-pressed', String((button.getAttribute('data-fit-size') ?? '') === this.size))
    );

    const rows = bySearch.filter((f) => !this.make || f.make === this.make);
    const container = this.querySelector('[data-fit-rows]');
    if (!container) return;
    const groups = [...new Set(rows.map((f) => f.make))].sort();
    const out = [];
    for (const make of groups) {
      const list = rows.filter((f) => f.make === make);
      const head = document.createElement('div');
      head.className = 'dl-fit__group';
      head.setAttribute('role', 'row');
      const title = document.createElement('h3');
      title.setAttribute('role', 'rowheader');
      title.textContent = make;
      const count = document.createElement('span');
      count.className = 'dl-label dl-muted';
      count.textContent = ((list.length === 1 ? this.dataset.bedsOne : this.dataset.bedsOther) ?? '').replace('[n]', String(list.length));
      head.append(title, count);
      out.push(head);
      for (const fit of list) out.push(this.#row(fit));
    }
    container.replaceChildren(...out);
    /** @type {HTMLElement} */ (this.querySelector('[data-fit-none]')).hidden = rows.length > 0;
  }

  #row(fit) {
    const [price] = this.prices[fit.handle] ?? [0];
    const row = document.createElement('div');
    row.className = `dl-fit__row${fit.handle === this.picked ? ' is-picked' : ''}`;
    row.setAttribute('role', 'row');
    const cell = (className, text) => {
      const span = document.createElement('span');
      span.setAttribute('role', 'cell');
      if (className) span.className = className;
      span.textContent = text;
      return span;
    };
    const meta = document.createElement('span');
    meta.className = 'dl-fit__meta';
    const kit = cell('', '');
    const strong = document.createElement('strong');
    strong.textContent = `${fit.size}${price ? ` · ${this.#money(price)}` : ''}`;
    kit.append(strong);
    meta.append(cell('', fit.years), cell('', fit.bed || this.dataset.oneBed || ''), kit);
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `dl-btn dl-btn--xs ${fit.handle === this.picked ? '' : 'dl-btn--dark'}`;
    button.setAttribute('data-fit-pick', fit.handle);
    button.textContent = (fit.handle === this.picked ? this.dataset.picked : this.dataset.pick) ?? '';
    row.append(cell('dl-fit__model', this.#short(fit)), meta, button);
    return row;
  }
}

if (!customElements.get('dialed-fit-guide')) customElements.define('dialed-fit-guide', DialedFitGuide);
